import {
  buildSearchIndex,
  type SearchIndexArtifact,
} from "@apf/framework-config/search-index";
import type {
  FrameworkSnapshot,
  ViewerContext,
  ViewerLens,
} from "@apf/framework-config";

export interface BeyondWorkBridge {
  invokeTask<T = unknown>(taskName: string, inputArgs?: Record<string, unknown>, timeout?: number, outputName?: string): Promise<T>;
  getRoute?(): string | undefined;
  navigate?(route: string): void;
  onRouteChange?(handler: (route: string) => void): () => void;
}

export interface FrameworkDataProvider {
  getFrameworkSnapshot(): Promise<FrameworkSnapshot>;
  getSearchIndex?(): Promise<SearchIndexArtifact>;
}

/* ------------------------------------------------------------------------ *
 * Portable search delta (search decision memo, phase 4).
 * The get_search_index task returns the download-derived documents only; the
 * core index never crosses the package boundary because the browser rebuilds
 * it from the snapshot the package already ships. This block is the Workblock
 * half of the provider contract; ApiFrameworkProvider (the service half)
 * lives in apps/ux-app/src/framework.tsx.
 * ------------------------------------------------------------------------ */

/** The get_search_index task payload: download-derived documents, no core docs. */
export interface PortableSearchIndexDelta {
  doNotEdit: true;
  schemaVersion: string;
  audienceId: SearchIndexArtifact["audienceId"];
  sourceContentHash: string;
  assetDocuments: SearchIndexArtifact["assetDocuments"];
  assetTokens: SearchIndexArtifact["assetTokens"];
  exact: SearchIndexArtifact["exact"];
}

export class WorkblockFrameworkProvider implements FrameworkDataProvider {
  private searchIndexPromise?: Promise<SearchIndexArtifact>;

  constructor(private readonly bw: BeyondWorkBridge) {}

  async getFrameworkSnapshot(): Promise<FrameworkSnapshot> {
    return this.bw.invokeTask<FrameworkSnapshot>("get_framework_snapshot", {}, 30_000, "framework_snapshot");
  }

  /**
   * SearchPage reads this property and calls it unbound, so it stays an arrow
   * function. It runs lazily when /search first mounts — never on the
   * first-paint path — and memoises the merged artifact; a failed load clears
   * the memo so the next /search mount retries.
   */
  readonly getSearchIndex = (): Promise<SearchIndexArtifact> => {
    this.searchIndexPromise ??= this.loadSearchIndex().catch((error) => {
      this.searchIndexPromise = undefined;
      throw error;
    });
    return this.searchIndexPromise;
  };

  private async loadSearchIndex(): Promise<SearchIndexArtifact> {
    const [snapshot, delta] = await Promise.all([
      this.getFrameworkSnapshot(),
      this.bw.invokeTask<PortableSearchIndexDelta>("get_search_index", {}, 30_000, "search_index"),
    ]);
    const core = buildSearchIndex({ snapshot });
    /* A delta from another audience, schema or snapshot is discarded rather
     * than merged: the snapshot-built core still serves governed results,
     * matching the fail-soft shape of the site's stale-artifact fallback. */
    if (
      delta.audienceId !== core.audienceId ||
      delta.schemaVersion !== core.schemaVersion ||
      delta.sourceContentHash !== core.sourceContentHash
    ) {
      return core;
    }
    const exact: Record<string, string[]> = { ...core.exact };
    for (const [term, keys] of Object.entries(delta.exact)) {
      exact[term] = [...new Set([...(exact[term] ?? []), ...keys])].sort();
    }
    return {
      ...core,
      assetDocuments: delta.assetDocuments,
      assetTokens: delta.assetTokens,
      exact,
    };
  }
}

/* --------------------------- end search delta ---------------------------- */

export class StaticFrameworkProvider implements FrameworkDataProvider {
  constructor(private readonly snapshot: FrameworkSnapshot) {}
  async getFrameworkSnapshot(): Promise<FrameworkSnapshot> { return this.snapshot; }
}

export function viewerContextFromSnapshot(snapshot: FrameworkSnapshot): ViewerContext {
  const role = snapshot.allowedLenses[0] as ViewerLens | undefined;
  if (!role) throw new Error("The framework snapshot does not declare an allowed viewer lens.");
  return {
    partnerId: snapshot.partnerId,
    audienceId: snapshot.audienceId,
    role,
    locale: snapshot.locale,
    sessionLabel: snapshot.audienceId
  };
}
